import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { Play, UserCircle } from "lucide-react";
import axios from "axios";
import { getDeviceId, getDeviceName } from "../utils/deviceId";
import { trackPlaybackEvent } from "../services/playbackService";

/* =======================
   Botón de Cast/AirPlay
   ======================= */
const CastButton = ({ episode, series }) => {
  const [isCastInitialized, setIsCastInitialized] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [castingMethod, setCastingMethod] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("");

  useEffect(() => {
    if (isCastInitialized) return;

    if (window.cast && window.cast.framework) {
      try {
        initializeCastApi();
        setIsCastInitialized(true);
      } catch (error) {
        console.error("Error al inicializar directamente:", error);
      }
      return;
    }

    if (!window["__onGCastApiAvailable"]) {
      window["__onGCastApiAvailable"] = function (isAvailable) {
        if (isAvailable && !isCastInitialized) {
          try {
            initializeCastApi();
            setIsCastInitialized(true);
          } catch (error) {
            console.error(
              "Error al inicializar Google Cast en callback:",
              error
            );
          }
        }
      };
    }

    loadCastScript();
  }, [isCastInitialized]);

  const [castScriptLoaded, setCastScriptLoaded] = useState(false);

  const loadCastScript = () => {
    if (castScriptLoaded || document.getElementById("cast-api-script")) return;
    setCastScriptLoaded(true);
    const script = document.createElement("script");
    script.id = "cast-api-script";
    script.src =
      "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
    document.body.appendChild(script);
  };

  const initializeCastApi = () => {
    if (isCastInitialized) return;
    if (!window.chrome || !window.chrome.cast) {
      console.error("La API de Chrome Cast no está disponible");
      return;
    }
    try {
      if (
        window.cast &&
        window.cast.framework &&
        window.cast.framework.CastContext &&
        window.cast.framework.CastContext.getInstance
      ) {
        return;
      }
      const applicationId =
        window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
      const sessionRequest = new window.chrome.cast.SessionRequest(
        applicationId
      );
      const apiConfig = new window.chrome.cast.ApiConfig(
        sessionRequest,
        (session) => {
          setConnectionStatus(
            `Conectado a ${session.getCastDevice().friendlyName}`
          );
          if (episode && episode.videoUrl)
            loadMediaToCast(session, episode, series);
        },
        (availability) => {},
        window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
      );
      window.chrome.cast.initialize(
        apiConfig,
        () => {},
        (e) => console.error(e)
      );
    } catch (error) {
      console.error("Error al inicializar la API de Cast:", error);
    }
  };

  const loadMediaToCast = (session, episodeData, seriesData) => {
    if (!session) return;
    try {
      const mediaUrl = episodeData.videoUrl || "";
      const episodeName = episodeData.name || "Episodio";
      const seriesName = seriesData?.name || "Serie";
      const posterUrl = episodeData.poster || seriesData?.poster || "";

      const mediaInfo = new chrome.cast.media.MediaInfo(mediaUrl, "video/mp4");
      const metadata = new chrome.cast.media.TvShowMediaMetadata();
      metadata.title = episodeName;
      metadata.seriesTitle = seriesName;
      metadata.season = episodeData.seasonNumber;
      metadata.episode = episodeData.episodeNumber;
      if (posterUrl) metadata.images = [{ url: posterUrl }];

      mediaInfo.metadata = metadata;
      mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;

      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      session
        .loadMedia(request)
        .then(() => setConnectionStatus(`Reproduciendo "${episodeName}" en TV`))
        .catch((error) => {
          console.error("Error al cargar contenido en Chromecast:", error);
          setConnectionStatus("Error al cargar el contenido en TV");
        });
    } catch (error) {
      console.error("Error en el proceso de carga de medios:", error);
    }
  };

  const toggleHelp = () => setShowHelp(!showHelp);

  const handleChromecastClick = () => {
    setCastingMethod("chromecast");

    const isChrome =
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isBrave =
      navigator.brave !== undefined ||
      (!isChrome && /Chrome/.test(navigator.userAgent));
    const isEdge = /Edg/.test(navigator.userAgent);

    if (!window.cast || !window.cast.framework) {
      if (isBrave)
        setConnectionStatus(
          "Brave puede bloquear Google Cast. Intenta con Chrome o permite la API en Escudos."
        );
      else if (isEdge)
        setConnectionStatus(
          "Edge puede tener limitaciones con Cast. Recomendamos Chrome."
        );
      else if (!isChrome)
        setConnectionStatus(
          "Google Cast es plenamente compatible con Google Chrome."
        );
      else
        setConnectionStatus("Google Cast no está disponible en este navegador");
      setIsConnecting(true);
      setTimeout(() => setIsConnecting(false), 5000);
      return;
    }

    try {
      setIsConnecting(true);
      setConnectionStatus("Preparando conexión a Chromecast...");

      const CastContext = window.cast.framework.CastContext;
      const context = CastContext.getInstance();
      context.setOptions({
        receiverApplicationId:
          window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });

      const handleSessionStart = () => {
        const session = context.getCurrentSession();
        if (session && episode && episode.videoUrl) {
          const mediaInfo = new window.chrome.cast.media.MediaInfo(
            episode.videoUrl,
            "video/mp4"
          );
          if (episode.name || series?.name) {
            const metadata = new window.chrome.cast.media.TvShowMediaMetadata();
            metadata.title = episode.name || "Episodio";
            metadata.seriesTitle = series?.name || "Serie";
            metadata.season = episode.seasonNumber;
            metadata.episode = episode.episodeNumber;
            if (episode.poster || series?.poster)
              metadata.images = [{ url: episode.poster || series?.poster }];
            mediaInfo.metadata = metadata;
          }
          mediaInfo.streamType = window.chrome.cast.media.StreamType.BUFFERED;
          const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
          setTimeout(() => {
            session
              .loadMedia(request)
              .then(() =>
                setConnectionStatus(
                  `Reproduciendo "${episode.name || "episodio"}" en TV`
                )
              )
              .catch((err) => {
                console.error("Error al cargar contenido:", err);
                setConnectionStatus("Error al cargar contenido en TV");
              });
          }, 1000);
        }
      };

      const SessionState = window.cast.framework.SessionState;
      context.addEventListener(
        window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event) => {
          switch (event.sessionState) {
            case SessionState.SESSION_STARTED:
              handleSessionStart();
              break;
            case SessionState.SESSION_ENDED:
              setConnectionStatus("Desconectado");
              setTimeout(() => setIsConnecting(false), 1000);
              break;
            case SessionState.SESSION_RESUMED:
              handleSessionStart();
              break;
          }
        }
      );

      setConnectionStatus("Selecciona un dispositivo Chromecast...");
      context
        .requestSession()
        .then(() => {})
        .catch((error) => {
          if (error.code === "cancel")
            setConnectionStatus("Selección cancelada");
          else setConnectionStatus("No se pudo conectar al dispositivo");
          setTimeout(() => setIsConnecting(false), 2000);
        });
    } catch (error) {
      console.error("Error general en Chromecast:", error);
      setConnectionStatus("Error al iniciar Chromecast");
      setTimeout(() => setIsConnecting(false), 2000);
    }
  };

  const handleAirPlayClick = () => {
    setCastingMethod("airplay");
    try {
      setIsConnecting(true);
      setConnectionStatus("Preparando AirPlay...");
      const videoElement = document.querySelector("video");
      if (videoElement && videoElement.webkitShowPlaybackTargetPicker) {
        videoElement.webkitShowPlaybackTargetPicker();
        setConnectionStatus("Selecciona tu dispositivo Apple TV");
      } else {
        setConnectionStatus("Tu navegador no es compatible con AirPlay");
        setTimeout(() => setIsConnecting(false), 2000);
      }
    } catch (error) {
      console.error("Error al iniciar AirPlay:", error);
      setConnectionStatus("Error al iniciar AirPlay");
      setTimeout(() => setIsConnecting(false), 2000);
    }
  };

  return (
    <div className="cast-button-wrapper relative">
      <div className="flex space-x-2">
        {isConnecting && (
          <div className="connection-status flex items-center mr-3">
            <div
              className={`pulse-dot w-2 h-2 rounded-full ${
                connectionStatus.includes("Error")
                  ? "bg-red-500"
                  : "bg-blue-500"
              } mr-2`}
            />
            <span className="text-xs text-white">{connectionStatus}</span>
          </div>
        )}
        <button
          onClick={handleChromecastClick}
          className={`${
            castingMethod === "chromecast" && isConnecting
              ? "bg-blue-600"
              : "bg-gray-700 hover:bg-gray-600"
          } text-white px-3 py-1 rounded transition-colors`}
          title="Transmitir a Chromecast"
          disabled={isConnecting && castingMethod !== "chromecast"}
        >
          Chromecast
        </button>
        <button
          onClick={handleAirPlayClick}
          className={`${
            castingMethod === "airplay" && isConnecting
              ? "bg-blue-600"
              : "bg-gray-700 hover:bg-gray-600"
          } text-white px-3 py-1 rounded transition-colors`}
          title="Transmitir con AirPlay"
          disabled={isConnecting && castingMethod !== "airplay"}
        >
          AirPlay
        </button>
        <button
          onClick={toggleHelp}
          className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
          title="Ayuda"
        >
          ?
        </button>
      </div>

      {showHelp && (
        <div className="help-panel absolute bg-black text-white p-4 rounded-md shadow-lg z-10 mt-2 max-w-md">
          <button
            className="absolute top-2 right-2 text-white hover:text-gray-300"
            onClick={() => setShowHelp(false)}
          >
            X
          </button>
          <h3 className="text-lg font-bold mb-2">Cómo transmitir a tu TV</h3>
          <div className="mb-3">
            <h4 className="font-bold">Chromecast:</h4>
            <ol className="list-decimal pl-5 text-sm">
              <li>Conecta el Chromecast a la misma WiFi.</li>
              <li>Usa Google Chrome.</li>
              <li>Haz clic en "Chromecast" y elige el dispositivo.</li>
            </ol>
          </div>
          <div>
            <h4 className="font-bold">AirPlay:</h4>
            <ol className="list-decimal pl-5 text-sm">
              <li>Apple TV / TV con AirPlay en la misma red.</li>
              <li>Usa Safari en macOS o iOS.</li>
              <li>Haz clic en "AirPlay" y selecciona tu dispositivo.</li>
            </ol>
          </div>
          <p className="text-xs mt-3 text-gray-400">
            Si hay problemas, revisa que tu red no bloquee la detección.
          </p>
        </div>
      )}
    </div>
  );
};

/* =======================
   SeriesPlayer
   ======================= */
const SeriesPlayer = ({ episode, series }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const [hasActiveSubscription, setHasActiveSubscription] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [episodeData, setEpisodeData] = useState(null);
  const [seriesData, setSeriesData] = useState(null);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // Tracking
  const lastTrackingRef = useRef(Date.now());
  const episodeDurationRef = useRef(0);
  const currentPositionRef = useRef(0);
  const trackingIntervalRef = useRef(null);
  const [trackingStatus, setTrackingStatus] = useState(null);

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token")
  );

  const TRACKING_INTERVAL = 30000;
  const TRACKING_THRESHOLD = 5;

  // Cargar los datos del episodio
  useEffect(() => {
    if (episode) {
      console.log("SeriesPlayer: Episodio recibido:", episode);
      setEpisodeData(episode);
    } else {
      console.warn("SeriesPlayer: No se recibió un episodio válido");
    }

    if (series) {
      console.log("SeriesPlayer: Serie recibida:", series);
      setSeriesData(series);
    } else {
      console.warn("SeriesPlayer: No se recibió una serie válida");
    }
  }, [episode, series]);

  // Verificar suscripción
  useEffect(() => {
    const checkSubscription = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsAuthenticated(false);
        setCheckingSubscription(false);
        return;
      }

      if (user?.role === "admin") {
        setHasActiveSubscription(true);
        setCheckingSubscription(false);
        return;
      }

      try {
        if (!user?.id) {
          setCheckingSubscription(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000";
        const response = await axios.get(
          `${apiUrl}/api/v1/userSubscriptions/${user.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        setSubscriptions(response.data);

        if (Array.isArray(response.data) && response.data.length > 0) {
          const now = new Date();
          const active = response.data.find(
            (sub) =>
              sub.status === "active" &&
              new Date(sub.startDate) <= now &&
              new Date(sub.endDate) > now
          );
          setHasActiveSubscription(Boolean(active));
        } else {
          setHasActiveSubscription(false);
        }
      } catch (error) {
        console.error("Error al obtener información de suscripciones:", error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [user?.id, user?.role]);

  // Inicializar el reproductor de video
  useEffect(() => {
    console.log("SeriesPlayer - Props recibidas:", { episode, series });

    // Validación adicional para episodio y URL de video
    if (!episode) {
      console.error("SeriesPlayer - No se recibió objeto de episodio");
      return;
    }

    if (!episode.videoUrl) {
      console.error(
        "SeriesPlayer - El episodio no contiene URL de video:",
        episode
      );
      return;
    }

    console.log("SeriesPlayer - URL de video del episodio:", episode.videoUrl);

    // Validar y corregir la URL del video
    if (episode.videoUrl) {
      // Verificar si la URL es relativa y necesita la base URL
      if (episode.videoUrl.startsWith("/")) {
        const baseUrl =
          process.env.REACT_APP_API_URL || "http://localhost:4000";
        episode.videoUrl = `${baseUrl}${episode.videoUrl}`;
        console.log("SeriesPlayer - URL corregida con base:", episode.videoUrl);
      }

      // Asegurar que la URL sea absoluta
      if (
        !episode.videoUrl.startsWith("http://") &&
        !episode.videoUrl.startsWith("https://")
      ) {
        const baseUrl =
          process.env.REACT_APP_API_URL || "http://localhost:4000";
        episode.videoUrl = `${baseUrl}/${episode.videoUrl.replace(/^\//, "")}`;
        console.log(
          "SeriesPlayer - URL convertida a absoluta:",
          episode.videoUrl
        );
      }

      // Verificar que no haya dobles slashes excepto después del protocolo
      episode.videoUrl = episode.videoUrl
        .replace(/:\/\/+/g, "://")
        .replace(/([^:])\/+/g, "$1/");
      console.log("SeriesPlayer - URL final a usar:", episode.videoUrl);
    }

    let player;
    const videoElement = videoRef.current;

    // Agregar logs para depuración
    console.log("SeriesPlayer: Inicializando reproductor con:", {
      episodeAvailable: !!episode,
      videoUrl: episode?.videoUrl,
      videoElement: !!videoElement,
    });

    if (episode?.videoUrl && videoElement) {
      try {
        // Reforzar controles
        videoElement.setAttribute("controlsList", "nodownload nofullscreen");
        videoElement.setAttribute("disablePictureInPicture", "true");
        videoElement.setAttribute("oncontextmenu", "return false;");

        player = videojs(videoElement, {
          controls: true,
          autoplay: false,
          preload: "auto",
          bigPlayButton: true,
          controlBar: {
            pictureInPictureToggle: false,
            downloadButton: false,
            fullscreenToggle: true,
            remainingTimeDisplay: false,
            timeDivider: false,
            durationDisplay: false,
            titleDisplay: false,
          },
          userActions: {
            hotkeys: false,
          },
          sources: [
            {
              src: episode.videoUrl,
              type: "video/mp4",
            },
          ],
        });

        console.log(
          "SeriesPlayer: Reproductor VideoJS inicializado correctamente"
        );
        playerRef.current = player;

        player.on("loadedmetadata", () => {
          const durationInSeconds = Math.round(player.duration());
          episodeDurationRef.current = durationInSeconds;
          console.log(`Video metadata loaded. Duration: ${durationInSeconds}s`);
        });

        player.on("error", (e) => {
          console.error("Error en el reproductor de video:", e);
          console.error(
            "Código de error:",
            player.error ? player.error().code : "No disponible"
          );
        });

        player.on("play", () => {
          const currentTime = Math.round(player.currentTime());
          currentPositionRef.current = currentTime;

          const eventType = currentTime === 0 ? "start" : "resume";
          trackPlaybackEvent(
            episode.id,
            eventType,
            currentTime,
            episodeDurationRef.current,
            "episode"
          );
          startTrackingInterval();
        });

        player.on("pause", () => {
          const currentTime = Math.round(player.currentTime());
          currentPositionRef.current = currentTime;
          trackPlaybackEvent(
            episode.id,
            "pause",
            currentTime,
            episodeDurationRef.current,
            "episode"
          );
          clearTrackingInterval();
        });

        player.on("seeking", () => {
          const currentTime = Math.round(player.currentTime());
          if (
            Math.abs(currentTime - currentPositionRef.current) >
            TRACKING_THRESHOLD
          ) {
            trackPlaybackEvent(
              episode.id,
              "seek",
              currentTime,
              episodeDurationRef.current,
              "episode"
            );
            currentPositionRef.current = currentTime;
          }
        });

        player.on("ended", () => {
          const currentTime = Math.round(player.currentTime());
          currentPositionRef.current = currentTime;
          trackPlaybackEvent(
            episode.id,
            "end",
            currentTime,
            episodeDurationRef.current,
            "episode"
          );
          clearTrackingInterval();
        });

        if (
          navigator.mediaDevices &&
          typeof navigator.mediaDevices.getDisplayMedia === "function"
        ) {
          const originalGetDisplayMedia =
            navigator.mediaDevices.getDisplayMedia;
          navigator.mediaDevices.getDisplayMedia = function (constraints) {
            if (videoElement && !videoElement.paused) {
              player.pause();
              alert(
                "La grabación de pantalla no está permitida durante la reproducción"
              );
            }
            return originalGetDisplayMedia.call(this, constraints);
          };
        }

        // // Solución agresiva para eliminar títulos que causan titileo
        // const removeUnwantedElements = () => {
        //   // Seleccionar todos los posibles contenedores
        //   const videoContainer = document.querySelector('.video-js');
        //   if (!videoContainer) return;

        //   // Buscar y eliminar elementos con texto de episodio
        //   const elementsToCheck = videoContainer.querySelectorAll('*');
        //   elementsToCheck.forEach(el => {
        //     if (el.textContent && (
        //         el.textContent.includes('Episodio') ||
        //         el.textContent.includes('T1') ||
        //         el.textContent.includes('- T')
        //       )) {
        //       // Primero intentar ocultar completamente
        //       el.style.cssText = 'display: none !important; opacity: 0 !important; visibility: hidden !important; height: 0 !important; width: 0 !important; overflow: hidden !important;';

        //       // Si es posible, remover completamente
        //       if (el.parentNode) {
        //         try {
        //           el.parentNode.removeChild(el);
        //         } catch (e) {
        //           console.log('No se pudo eliminar elemento', e);
        //         }
        //       }
        //     }
        //   });

        //   // Buscar específicamente elementos de título
        //   const titleElements = videoContainer.querySelectorAll('.vjs-title-bar, .vjs-text-track-display, .vjs-text-track-settings, .vjs-title-display');
        //   titleElements.forEach(el => {
        //     if (el.parentNode) {
        //       try {
        //         el.parentNode.removeChild(el);
        //       } catch (e) {
        //         console.log('No se pudo eliminar elemento de título', e);
        //       }
        //     }
        //   });
        // };

        // // Ejecutar inmediatamente
        // removeUnwantedElements();

        // // Y luego en intervalos regulares
        // const removalInterval = setInterval(removeUnwantedElements, 100);

        // // Limpiar en desmontaje
        // playerRef.current.on('dispose', () => {
        //   clearInterval(removalInterval);
        // });

        // // También monitorear cambios en el DOM
        // const observer = new MutationObserver(() => {
        //   removeUnwantedElements();
        // });

        // // Iniciar observación
        // const videoContainer = document.querySelector('.video-js');
        // if (videoContainer) {
        //   observer.observe(videoContainer, {
        //     childList: true,
        //     subtree: true,
        //     attributes: true,
        //     characterData: true
        //   });

        //   // Limpiar en desmontaje
        //   playerRef.current.on('dispose', () => {
        //     observer.disconnect();
        //   });
        // }

        // // Agregar CSS global para ocultar cualquier elemento de título
        // const styleElement = document.createElement('style');
        // styleElement.textContent = `
        //   .video-js .vjs-title-bar,
        //   .video-js .vjs-text-track-display,
        //   .video-js .vjs-text-track-settings,
        //   .video-js .vjs-title-display,
        //   .video-js [class*="title"],
        //   .video-js [class*="caption"],
        //   .video-js [class*="track"],
        //   .video-js [class*="text"] {
        //     display: none !important;
        //     opacity: 0 !important;
        //     visibility: hidden !important;
        //     height: 0 !important;
        //     overflow: hidden !important;
        //   }
        // `;
        // document.head.appendChild(styleElement);
      } catch (error) {
        console.error("Error al inicializar el reproductor VideoJS:", error);
      }
    } else {
      console.warn(
        "SeriesPlayer: No se puede inicializar el reproductor - falta URL del video o elemento de video"
      );
    }

    const startTrackingInterval = () => {
      if (trackingIntervalRef.current)
        clearInterval(trackingIntervalRef.current);

      trackingIntervalRef.current = setInterval(() => {
        if (playerRef.current && !playerRef.current.paused()) {
          const currentTime = Math.round(playerRef.current.currentTime());
          if (
            Math.abs(currentTime - currentPositionRef.current) >
            TRACKING_THRESHOLD
          ) {
            trackPlaybackEvent(
              episode.id,
              "update",
              currentTime,
              episodeDurationRef.current,
              "episode"
            );
            currentPositionRef.current = currentTime;
            lastTrackingRef.current = Date.now();
            setTrackingStatus(`Tracking: ${currentTime}s`);
          }
        }
      }, TRACKING_INTERVAL);
    };

    const clearTrackingInterval = () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };

    return () => {
      if (player && player.dispose) player.dispose();
      clearTrackingInterval();

      if (
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getDisplayMedia === "function" &&
        navigator.mediaDevices.getDisplayMedia
          .toString()
          .includes("player.pause")
      ) {
        delete navigator.mediaDevices.getDisplayMedia;
      }
    };
  }, [episode]);

  // Estilos para Cast
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      google-cast-launcher { --connected-color: #1db954; --disconnected-color: white; }
      .cast-controls { display: flex; align-items: center; margin-top: 8px; }
      .cast-button-wrapper { display: flex; align-items: center; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (checkingSubscription)
    return (
      <div className="bg-black w-full h-screen flex items-center justify-center">
        <h1 className="text-white text-2xl">Cargando episodio...</h1>
      </div>
    );

  const renderAuthenticationMessage = () => (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-black to-gray-900 p-4">
      <div className="bg-black/60 p-6 rounded-lg max-w-md text-center">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-white">Acceso Exclusivo</h2>
        <p className="text-gray-300 mb-6">
          Para disfrutar de este contenido necesitas iniciar sesión o
          registrarte.
        </p>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    localStorage.setItem("nextAfterLogin", window.location.pathname);
    return renderAuthenticationMessage();
  }

  const renderNoSubscriptionMessage = () => (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-black to-gray-900 p-4">
      <div className="bg-black/60 p-6 rounded-lg max-w-md text-center">
        <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="w-8 h-8 text-black" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-white">
          Suscripción Requerida
        </h2>
        <p className="text-gray-300 mb-4">
          Necesitas una suscripción vigente para reproducir episodios de series.
        </p>
        {subscriptions && subscriptions.length > 0 ? (
          <div className="mb-4 text-left text-sm bg-gray-800/50 p-3 rounded">
            <p className="font-semibold text-white mb-1">
              Estado de tus suscripciones:
            </p>
            {subscriptions.map((sub) => (
              <div key={sub.id} className="mb-2 text-gray-300">
                <p>
                  • Estado:{" "}
                  <span
                    className={
                      sub.status === "active"
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {sub.status}
                  </span>
                </p>
                <p>
                  • Válida: {new Date(sub.startDate).toLocaleDateString()} a{" "}
                  {new Date(sub.endDate).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 mb-4">
            No tienes ninguna suscripción registrada.
          </p>
        )}
        <Link
          to="/subscriptionplan"
          className="block w-full bg-yellow-500 hover:bg-yellow-400 text-black font-medium py-2 px-4 rounded transition-colors"
        >
          Ver planes disponibles
        </Link>
      </div>
    </div>
  );

  if (!hasActiveSubscription) return renderNoSubscriptionMessage();

  if (!episode || !episode.videoUrl) {
    console.error("SeriesPlayer: No hay episodio o URL de video disponible", {
      episode,
    });
    return (
      <div className="relative w-full min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <div className="text-xl mb-4">No hay video disponible</div>
        <p className="text-gray-400 mb-4">
          No se pudo cargar el video del episodio
        </p>
        <button
          onClick={() => navigate(-1)}
          className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg hover:bg-opacity-70 mt-4"
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-black" ref={containerRef}>
      <div className="absolute inset-0 z-10 bg-cover bg-center"></div>

      <div className="flex flex-col items-center justify-center w-full h-full p-4 box-border z-10 relative">
        {episode?.description && (
          <p className="text-lg text-gray-300 mb-8">{episode.description}</p>
        )}

        {/* Contenedor del video */}
        <div className="relative w-full">
          <video
            ref={videoRef}
            className="video-js vjs-default-skin w-full h-[80vh] object-cover"
            controls
            controlsList="nodownload nofullscreen"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Fuente del video */}
            <source src={episode.videoUrl} type="video/mp4" />
            {/* Pista de subtítulos */}
            {episode?.id && (
              <track
                key={episode.id}
                label="Español"
                kind="subtitles"
                srcLang="es"
                src={`${
                  process.env.REACT_APP_API_URL || "http://localhost:4000"
                }/api/v1/subtitles/episode/${episode.id}.vtt`}
                default
              />
            )}
            Tu navegador no soporta la reproducción de videos.
          </video>

          {/* Controles inferiores */}
          <div className="w-full bg-gray-900 p-3 flex items-center justify-between">
            <div className="cast-controls flex items-center">
              <CastButton episode={episode} series={series} />
            </div>

            {trackingStatus && (
              <div className="text-white text-sm">{trackingStatus}</div>
            )}

            <button
              onClick={() => navigate(-1)}
              className="text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg hover:bg-opacity-70"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesPlayer;
